import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import type { PressEvent } from 'react-aria';
import { Provider, useSlottedContext } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { assert } from '@gitbook/util-ts';

import { Actions } from '../Actions';
import { AnimatedStateContext } from '../animations';
import { Backdrop } from '../Backdrop';
import { Button, type ButtonProps } from '../Button';
import { DescriptionContext } from '../Description';
import { HeadingContext } from '../Heading';
import {
    LabeledContainerLevelContext,
    LabeledContainerLevelContextDefault,
} from '../LabeledContainer';
import {
    Overlay,
    OverlayCloseButton,
    type OverlayCloseButtonProps,
    OverlayContext,
    type OverlayProps,
    OverlayTrigger,
    type OverlayTriggerProps,
    useOverlayAction,
    useOverlayClose,
} from '../Overlay';
import { Portal } from '../Portal';
import { pxToRem } from '../util';
import { DialogStackProvider, useStackDialog } from './DialogStack';

export { OverlayRoot as DialogRoot } from '../Overlay';

export type DialogTriggerProps = Omit<OverlayTriggerProps, 'ariaHasPopup'>;

export function DialogTrigger(props: DialogTriggerProps) {
    return (
        <OverlayTrigger trigger={props.trigger} ariaHasPopup="dialog">
            {props.children}
        </OverlayTrigger>
    );
}

type AbstractDialogProps = {
    /**
     * The role of the dialog.
     */
    role: 'dialog' | 'alertdialog';

    /**
     * Set the size of the dialog.
     * - `full-screen` will make the dialog take the full screen.
     * - `auto` will make the dialog take the size of its content.
     * - `{ width?, height? }` sets the dialog to a specific width, height or both.
     * - `undefined` will make the dialog take the default size.
     */
    size?:
        | 'full-screen'
        | 'auto'
        | { width?: string | number; height?: string | number }
        | undefined;

    children: React.ReactNode;

    className?: string;
} & Pick<OverlayProps, 'contain' | 'autoFocus' | 'restoreFocus' | 'isDismissable'>;

/**
 * Abstract dialog that exposes a "role" prop and contains dialog logic.
 */
function AbstractDialog(props: AbstractDialogProps) {
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Dialog must be used within a DialogRoot');

    const [positionInStack, isRoot] = useStackDialog({ id: context.id, animatedState });

    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';

    /* We visualize max 3 dialogs in the stack. Any more will take the position of the third, but question the design if you encounter this as stacking dialogs isn't the best UX. */
    const maxPositionInStack = Math.min(positionInStack, 2);

    return (
        <Provider
            values={[
                [
                    HeadingContext,
                    { level: 2, id: headingId, className: 'text-ui-heading-large break-words' },
                ],
                [DescriptionContext, { id: descriptionId }],
                [LabeledContainerLevelContext, LabeledContainerLevelContextDefault],
            ]}
        >
            {isRoot && <Backdrop isExiting={isExiting} isEntering={isEntering} />}
            <Overlay
                data-spine-dialog=""
                contain={props.contain}
                autoFocus={props.autoFocus}
                restoreFocus={props.restoreFocus}
                isDismissable={props.isDismissable}
                role={props.role}
                id={context.id}
                aria-modal="true"
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                shouldCloseOnInteractOutside={(element) => {
                    // Dialog triggered by `useConfirm` are not considered as child of dialog
                    // the OverlayStack does not work for them, so we explicitely avoid closing if clicked.
                    return !element.closest('[data-spine-modal]:has([role=alertdialog])');
                }}
                style={
                    {
                        '--dialog-max-height': 'calc(100vh - 2rem)',
                        height:
                            typeof props.size === 'object' && props.size && props.size.height
                                ? `min(var(--dialog-max-height), ${pxToRem(props.size.height)})`
                                : undefined,
                        width:
                            typeof props.size === 'object' && props.size && props.size.width
                                ? '100%'
                                : undefined,
                        maxWidth:
                            typeof props.size === 'object' && props.size && props.size.width
                                ? pxToRem(props.size.width)
                                : undefined,
                    } as React.CSSProperties
                }
                className={clsx(
                    'group/dialog',
                    'relative max-h-(--dialog-max-height) shrink-0 overflow-x-auto rounded-lg bg-base shadow-overlay will-change-transform dark:border dark:border-neutral-subtle',
                    'group-has-[[data-spine-dialog-panel-layout]]/dialog:flex group-has-[[data-spine-dialog-panel-layout]]/dialog:flex-col group-has-[[data-spine-dialog-panel-layout]]/dialog:overflow-y-hidden',
                    'transition [transition-delay:30ms] [transition-duration:150ms] [transition-timing-function:ease-out] fill-mode-forwards',
                    !props.size && 'w-full max-w-xl',
                    props.size === 'full-screen' && 'size-full',
                    isEntering && 'animate-in fade-in slide-in-from-top-2 zoom-in-95',
                    isExiting && 'animate-out fade-out slide-out-to-top-2 zoom-out-95',
                    /* Scale and translate on Y-axis to make the stack order visual */
                    maxPositionInStack === 1 && '-translate-y-8 scale-95 brightness-75',
                    maxPositionInStack === 2 && '-translate-y-16 scale-90 brightness-50',
                    props.className
                )}
            >
                {context.isClosable ? (
                    <div className="sticky top-0 z-10">
                        <DialogCloseButton
                            data-spine-dialog-dismiss=""
                            className="absolute top-3 right-3 z-[1]"
                            size="small"
                            iconOnly
                            label="Close"
                            kind="blank"
                            leadingIcon={Icon.X}
                        />
                    </div>
                ) : null}
                {props.children}
            </Overlay>
        </Provider>
    );
}

type ModalProps = {
    children: React.ReactNode;
};

function Modal(props: ModalProps) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Dialog must be used within a DialogRoot');

    if (animatedState === 'exited') {
        return null;
    }

    return (
        <Portal>
            <div
                data-spine-modal=""
                className="fixed inset-0 z-overlay flex flex-col items-center p-4"
            >
                <div className="h-[8vh] shrink-[100]" />
                <DialogStackProvider>{props.children}</DialogStackProvider>
                <div className="h-[8vh] shrink-[100]" />
            </div>
        </Portal>
    );
}

export type DialogProps = Omit<AbstractDialogProps, 'role'>;

/**
 * Modal surface that overlays the app, captures focus, and renders the content underneath inactive.
 * Compose with {@link DialogHeader}, {@link DialogBody}, and {@link DialogFooter} for the default
 * layout, or with {@link DialogSplit} + {@link DialogMain}/{@link DialogAside}/
 * {@link DialogGradientAside} for multi-panel shells.
 *
 * Use {@link AlertDialog} instead when the dialog must demand a decision (confirm/destroy flows).
 * Use `Popover` for non-modal, transient contextual UI.
 */
export function Dialog(props: DialogProps) {
    return (
        <Modal>
            <AbstractDialog role="dialog" {...props} />
        </Modal>
    );
}

export type AlertDialogProps = {
    children?: React.ReactNode;
    className?: string;
};

/**
 * A modal dialog that interrupts the user with important content and expects a response.
 */
export function AlertDialog(props: AlertDialogProps) {
    return (
        <Modal>
            <AbstractDialog role="alertdialog" className={props.className}>
                {props.children}
            </AbstractDialog>
        </Modal>
    );
}

export type DialogHeaderProps = React.ComponentPropsWithoutRef<'div'>;

export function DialogHeader(props: DialogHeaderProps & { compact?: boolean }) {
    return (
        <div
            {...props}
            data-spine-dialog-header=""
            className={clsx(
                'flex flex-col gap-2',
                'group-has-[[data-spine-dialog-body]]/dialog:pb-0',
                props.compact ? 'p-4 md:p-6' : 'px-4 py-6 md:px-10 md:py-10',
                props.className
            )}
        />
    );
}

export type DialogBodyProps = React.ComponentPropsWithoutRef<'div'> & {
    /**
     * Set the body to be scrollable.
     * @default true
     */
    scrollable?: boolean;
};

export function DialogBody(props: DialogBodyProps) {
    const { scrollable = true, ...rest } = props;

    return (
        <Provider
            values={[
                [HeadingContext, {}],
                [DescriptionContext, {}],
            ]}
        >
            <div
                {...rest}
                data-spine-dialog-body=""
                className={clsx(
                    'relative box-border w-full px-4 py-6 md:px-10 md:py-10',
                    'group-has-[[data-spine-dialog-header]]/dialog:pt-6',
                    scrollable && 'scrollbar overflow-y-auto',
                    props.className
                )}
            />
        </Provider>
    );
}

export function DialogFooter(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <Actions
            {...props}
            data-spine-dialog-footer=""
            className={clsx(
                // The bottom needs to be a margin to stay "within"
                // the dialog and show its rounded corners.
                'sticky bottom-0 w-full border-t bg-base px-4 py-4',
                props.className
            )}
        />
    );
}

export function DialogFooterSecondary(props: React.ComponentPropsWithoutRef<'div'>) {
    return <div {...props} className={clsx('flex flex-1 items-center gap-4', props.className)} />;
}

export type DialogCloseButtonProps = Omit<OverlayCloseButtonProps, 'label'> & { label?: string };

export function DialogCloseButton(props: DialogCloseButtonProps) {
    return (
        <OverlayCloseButton
            {...props}
            label={props.label ?? 'Cancel'}
            kind={props.kind ?? 'secondary'}
        />
    );
}

function DialogAsideHeadingProvider(props: {
    children: React.ReactNode;
    headingClassName?: string;
}) {
    const headingProps = useSlottedContext(HeadingContext);

    return (
        <Provider
            values={[
                [
                    HeadingContext,
                    {
                        ...(headingProps ?? {}),
                        id: undefined,
                        className: props.headingClassName ?? headingProps?.className,
                    },
                ],
                [DescriptionContext, {}],
            ]}
        >
            {props.children}
        </Provider>
    );
}

/**
 * Primary content area rendered by composed dialog layouts.
 */
export function DialogMain(props: React.ComponentPropsWithoutRef<'div'>) {
    const { children, className, ...rest } = props;

    return (
        <div
            {...rest}
            data-spine-dialog-main=""
            className={clsx('flex min-h-0 min-w-0 flex-1 flex-col', className)}
        >
            {children}
        </div>
    );
}

/**
 * Secondary content area rendered by composed dialog layouts.
 */
export type DialogAsideProps = React.ComponentPropsWithoutRef<'aside'> & {
    /**
     * Override the heading styles for the aside panel.
     */
    headingClassName?: string;
};

/**
 * Secondary content area rendered by composed dialog layouts.
 */
export function DialogAside(props: DialogAsideProps) {
    const { children, className, headingClassName, ...rest } = props;

    return (
        <DialogAsideHeadingProvider headingClassName={headingClassName}>
            <aside
                {...rest}
                data-spine-dialog-aside=""
                className={clsx(
                    'flex min-h-0 w-full flex-col justify-start overflow-y-auto bg-side p-6 max-md:order-2 max-md:w-full max-md:shrink-0 max-md:border-t md:w-[18.75rem] md:shrink-0',
                    className
                )}
            >
                {children}
            </aside>
        </DialogAsideHeadingProvider>
    );
}

export type DialogGradientAsideProps = Omit<DialogAsideProps, 'children'> & {
    children?: React.ReactNode;
    backgroundSrc?: {
        light?: string;
        dark?: string;
    };
};

const DEFAULT_BACKGROUND = `linear-gradient(210deg, transparent 60%, var(--accent-3))`;

/**
 * Gradient aside panel rendered by composed dialog layouts.
 */
export function DialogGradientAside(props: DialogGradientAsideProps) {
    const {
        children,
        backgroundSrc,
        className,
        headingClassName = 'text-ui-heading-medium',
        style,
        ...rest
    } = props;

    return (
        <>
            <DialogAsideHeadingProvider headingClassName={headingClassName}>
                <aside
                    {...rest}
                    data-spine-dialog-aside=""
                    className={clsx(
                        'hidden min-h-0 flex-col justify-start self-stretch overflow-y-auto bg-base bg-(image:--light-bg) bg-cover bg-right-bottom bg-no-repeat p-7 md:flex md:w-56 md:shrink-0 lg:w-80 dark:bg-(image:--dark-bg)',
                        className
                    )}
                    style={
                        {
                            '--light-bg': backgroundSrc?.light
                                ? `url(${backgroundSrc.light})`
                                : DEFAULT_BACKGROUND,
                            '--dark-bg': backgroundSrc?.dark
                                ? `url(${backgroundSrc.dark})`
                                : DEFAULT_BACKGROUND,
                            ...style,
                        } as React.CSSProperties
                    }
                >
                    {children}
                </aside>
            </DialogAsideHeadingProvider>
            {backgroundSrc?.light ? (
                <link href={backgroundSrc.light} as="image" rel="preload" />
            ) : null}
            {backgroundSrc?.dark ? (
                <link href={backgroundSrc.dark} as="image" rel="preload" />
            ) : null}
        </>
    );
}

/**
 * Split dialog container that composes a main panel and one or more asides.
 */
export function DialogSplit(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            data-spine-dialog-panel-layout=""
            className={clsx(
                'flex min-h-0 flex-1 max-md:flex-col',
                'md:[&>[data-spine-dialog-aside]:first-child]:border-r',
                'md:[&>[data-spine-dialog-aside]:first-child]:border-l-0',
                'md:[&>[data-spine-dialog-aside]:last-child]:border-l',
                props.className
            )}
        >
            {props.children}
        </div>
    );
}

function renderDialogAside(aside: React.ReactNode) {
    if (aside === null || aside === undefined || aside === false) {
        return null;
    }

    if (
        React.isValidElement<DialogAsideProps | DialogGradientAsideProps>(aside) &&
        (aside.type === DialogAside || aside.type === DialogGradientAside)
    ) {
        return aside;
    }

    return <DialogAside>{aside}</DialogAside>;
}

/**
 * Explicitly applies the default dialog layout.
 */
export function DialogRegularLayout(props: { children: React.ReactNode }) {
    return <DialogMain>{props.children}</DialogMain>;
}

/**
 * Applies a split dialog layout with a secondary panel on either side.
 */
export interface DialogSplitLayoutProps {
    children: React.ReactNode;

    /**
     * Content rendered in the split aside.
     * Wrap it in `DialogAside` to customize the aside element.
     */
    aside?: React.ReactNode;

    /**
     * Side on which the aside should be rendered.
     * @default 'right'
     */
    asidePosition?: 'left' | 'right';
}

/**
 * Applies a split dialog layout with a secondary panel on either side.
 */
export function DialogSplitLayout(props: DialogSplitLayoutProps) {
    const { asidePosition = 'right' } = props;
    const aside = renderDialogAside(props.aside);

    return (
        <DialogSplit className="w-full">
            {asidePosition === 'left' ? aside : null}
            <DialogMain>{props.children}</DialogMain>
            {asidePosition !== 'left' ? aside : null}
        </DialogSplit>
    );
}

export interface DialogBrandedLayoutProps {
    children: React.ReactNode;

    /**
     * Configuration for the aside panel
     */
    aside?: {
        /** Content placed on top of the gradient */
        content?: React.ReactNode;

        /** Overrides for gradients */
        gradient?: {
            light: string;
            dark: string;
        };
    } | null;

    /**
     * Optional logo to display in the dialog.
     * Defaults to the GitBook logo.
     */
    logo?: React.ReactNode | false;
}

/**
 * Apply a 2-panel layout with a gradient on the left.
 */
export function DialogBrandedLayout({ children, aside, logo }: DialogBrandedLayoutProps) {
    const shouldRenderAside = aside !== null;
    const brandedAsideContent = aside?.content;
    const logoPanel = (
        <div className="shrink-0 px-4 pb-2 max-md:hidden md:px-10">
            {logo === false ? null : logo || <Icon.LogoSquare size={32} />}
        </div>
    );
    const mainContent = (
        <DialogMain
            className={clsx(
                'items-start pt-10',
                '[&_[data-spine-dialog-header]]:pt-2! [&_[data-spine-dialog-header]]:pb-0!',
                '[&_[data-spine-dialog-body]]:pt-2! [&_[data-spine-dialog-body]]:pb-6!'
            )}
        >
            {logoPanel}
            {children}
        </DialogMain>
    );

    if (!shouldRenderAside) {
        return mainContent;
    }

    return (
        <DialogSplit className="md:h-full">
            <DialogGradientAside backgroundSrc={aside?.gradient}>
                {brandedAsideContent}
            </DialogGradientAside>
            {mainContent}
        </DialogSplit>
    );
}

/**
 * The aside of a branded dialog.
 */
export function BrandedDialogAside(props: {
    children: React.ReactNode;
    backgroundSrc?: { light?: string; dark?: string };
}) {
    return (
        <DialogGradientAside
            backgroundSrc={props.backgroundSrc}
            className="justify-end p-5 md:min-h-full md:w-72 lg:w-72"
        >
            {props.children}
        </DialogGradientAside>
    );
}

export type DialogActionButtonProps = Omit<ButtonProps, 'onPress'> & {
    /**
     * The action to perform when the confirm button is pressed.
     */
    onPress: (
        event: PressEvent,
        options: {
            /**
             * Prevent the dialog from closing.
             */
            preventClose: () => void;
        }
    ) => void | Promise<void>;
};

export function DialogActionButton(props: DialogActionButtonProps) {
    const { onPress, ...rest } = props;
    const [isPending, startDialogAction] = useOverlayAction();
    const close = useOverlayClose();
    return (
        <Button
            {...rest}
            loadable={[isPending]}
            onPress={(event) => {
                let shouldClose = true;
                const promise = onPress(event, {
                    preventClose: () => {
                        shouldClose = false;
                    },
                });
                // If it returns a promise, wait for it to resolve before closing the dialog.
                if (promise) {
                    return startDialogAction(async ({ close }) => {
                        await promise;
                        if (shouldClose) {
                            await close();
                        }
                    });
                }
                if (shouldClose) {
                    close();
                }
            }}
        />
    );
}
